import { useTranslation } from "react-i18next";
import { daysSinceDone, want as wantOf } from "../core/computed";
import { round1 } from "../core/format";
import type { Task } from "../core/schema";
import { wantDisplay, wantLevel } from "../core/want";
import { Why } from "../ui";
import { formatPeriod } from "./period";
import { WantBar } from "./WantBar";

/**
 * A repeating task's want in a row: the bar, the number and its *Why?* (spec §9.5). The same in *Wants doing* and in
 * lists. `want`/`days` when the caller has them computed already.
 */
export function WantRow({
  task,
  nowMs,
  want,
  days,
  className,
}: {
  task: Task;
  nowMs: number;
  want?: number;
  days?: number | null;
  className?: string;
}) {
  const { t } = useTranslation();
  const w = want ?? wantOf(task as never, nowMs);
  const d = days !== undefined ? days : daysSinceDone(task as never, nowMs);
  const ceiling = task.props["rec.ceiling"] as number | undefined;
  return (
    <div className={`flex items-center gap-2 ${className ?? ""}`} data-testid="want-row">
      <WantBar className="w-40" value={wantDisplay(w, ceiling)} level={wantLevel(w)} ceiling={ceiling || 3} />
      <span className="font-mono text-[11px] text-neutral-500">{round1(w)}</span>
      <Why
        title={t("want.label")}
        formula="want = (age / period) ^ elasticity"
        rows={[
          [t("want.age"), `${round1(d ?? 0)} d`],
          [
            t("props.rec.periodDays"),
            formatPeriod(t, Number(task.props["rec.periodDays"]) || 7, task.props["rec.periodUnit"]),
          ],
          [t("props.rec.elasticity"), String(task.props["rec.elasticity"] ?? 1)],
        ]}
      />
    </div>
  );
}
